/** Which edge of the page the split-mode chat panel occupies. */
export type ChatSide = 'left' | 'right'

/**
 * Persisted layout preference for the chat panel's side.
 *
 * A COOKIE rather than `localStorage` — deliberately, and for the same
 * reason the shadcn sidebar persists its pin (`sidebar_pinned`) that way: the side
 * decides WHICH panel of the shell renders the chat, so a client-only read
 * would make the server paint the chat on the left and the client hydrate
 * it on the right (a structural hydration mismatch, plus a visible jump).
 * The server shell reads this cookie and seeds `GlobalChatProvider`, so the
 * very first paint is already on the user's chosen edge.
 *
 * (The panel WIDTH stays in `localStorage` — it only tweaks a style, never
 * the tree shape.)
 */
export const CHAT_SIDE_COOKIE_NAME = 'brew_chat_side'

/** One year, refreshed on every toggle. */
export const CHAT_SIDE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

export const DEFAULT_CHAT_SIDE: ChatSide = 'left'

export function isChatSide(
  value: string | null | undefined
): value is ChatSide {
  return value === 'left' || value === 'right'
}

/**
 * Normalize a raw cookie value (server or client) to a side. Anything
 * unexpected falls back to the default, so a corrupted cookie can never
 * strand the chat.
 */
export function resolveChatSide(value: string | null | undefined): ChatSide {
  return isChatSide(value) ? value : DEFAULT_CHAT_SIDE
}

/** Client-side read — used by tests and any non-server-seeded mount. */
export function readChatSideCookie(): ChatSide {
  if (typeof document === 'undefined') {
    return DEFAULT_CHAT_SIDE
  }
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${CHAT_SIDE_COOKIE_NAME}=([^;]*)`)
  )
  return resolveChatSide(match?.[1])
}

export function writeChatSideCookie(side: ChatSide): void {
  if (typeof document === 'undefined') {
    return
  }
  // biome-ignore lint/suspicious/noDocumentCookie: pre-Ultracite baseline
  document.cookie = `${CHAT_SIDE_COOKIE_NAME}=${side}; path=/; max-age=${CHAT_SIDE_COOKIE_MAX_AGE}; samesite=lax`
}

export function oppositeChatSide(side: ChatSide): ChatSide {
  return side === 'left' ? 'right' : 'left'
}
